import {
  CalendarClock,
  MessagesSquare,
  BriefcaseBusiness,
  ClipboardCheck,
  IndianRupee,
  Package,
  ReceiptText,
  History,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';

export type EmployeeNavSubChild = {
  to: string;
  label: string;
  managerOnly?: boolean;
  // Own payslip / own tax summary. TeamLio's panelCatalog deliberately has no
  // key for these routes, so no Access profile can take a person's own salary
  // away from them — the nav must not gate them either.
  selfService?: boolean;
  // Shown instead of `label` to someone who also has the org-wide Payroll
  // Records view, mirroring TeamLio's admin-vs-self Payroll submenu.
  adminLabel?: string;
  // Hide unless the viewer has MANAGE on this Access panel. Distinct from
  // `managerOnly`, which is a role check: an admin surface governed by the
  // Access grid must follow the grid, or a profile that withholds the panel
  // still advertises the page.
  panel?: string;
};

export type EmployeeNavChild = {
  to: string;
  label: string;
  Icon: LucideIcon;
  children?: EmployeeNavSubChild[];
};

export const EMPLOYEE_NAV_CHILDREN: EmployeeNavChild[] = [
  {
    to: '/employee/attendance',
    label: 'Attendance',
    Icon: CalendarClock,
    children: [
      { to: '/employee/attendance', label: 'Attendance' },
      // Shared-device attendance. Manage-level only: creating a kiosk mints a
      // credential many people punch from.
      { to: '/employee/attendance/kiosks', label: 'Kiosks', panel: 'set-attendance-kiosk' },
    ],
  },
  { to: '/employee/leaves', label: 'Leaves', Icon: ClipboardCheck },
  { to: '/employee/expenses', label: 'Expenses', Icon: ReceiptText },
  { to: '/employee/assets', label: 'Assets', Icon: Package },
  {
    to: '/employee/workflow/activity',
    label: 'Workflow',
    Icon: BriefcaseBusiness,
    children: [
      { to: '/employee/workflow/schedule', label: 'Work Schedule', managerOnly: true },
      { to: '/employee/workflow/activity', label: 'Activity Reports' },
      { to: '/employee/workflow/comparison', label: 'Activity Comparison', managerOnly: true },
    ],
  },
  {
    to: '/employee/payroll',
    label: 'Payroll',
    Icon: IndianRupee,
    children: [
      {
        to: '/employee/payroll',
        label: 'Payroll',
        adminLabel: 'Payroll Records',
        selfService: true,
      },
      { to: '/employee/payroll/tax', label: 'Tax Summary', selfService: true },
      { to: '/employee/payroll/approval', label: 'Payroll Approval' },
      { to: '/employee/payroll/reports', label: 'Reports' },
    ],
  },
  // Sits after Payroll, same position as hrpayroll's Sidebar.tsx — one
  // product, one running order in both apps.
  { to: '/employee/grievances', label: 'Feedback & Complaints', Icon: MessagesSquare },
  // Admin audit over everyone's login/logout/eviction history. Gated by the
  // `login_sessions` Access panel via accessPanels.ts, same as every other
  // entry here — the filter in EmployeeNavPanel/AppSidebar checks isPathVisible
  // against this real route. Every user's OWN sessions live at /active-devices
  // instead (ProfileTab), which stays ungated.
  { to: '/login-sessions', label: 'Login Sessions', Icon: History },
];

const LIMITED_EMPLOYEE_PATHS = new Set([
  '/employee/attendance',
  '/employee/leaves',
  '/employee/workflow/activity',
]);

export const employeeNavForWorkspace = (limited: boolean): EmployeeNavChild[] =>
  limited
    ? EMPLOYEE_NAV_CHILDREN.filter((item) => LIMITED_EMPLOYEE_PATHS.has(item.to))
    : EMPLOYEE_NAV_CHILDREN;

export const EMPLOYEE_ROUTE_PREFIXES = [
  '/employee/attendance',
  '/employee/leaves',
  '/employee/expenses',
  '/employee/assets',
  '/employee/workflow',
  '/employee/payroll',
  '/employee/grievances',
  // Not nested under /employee — mirrors hrpayroll's own top-level
  // /login-sessions route so the panelCatalog path stays identical cross-app.
  '/login-sessions',
];

export const isEmployeeRoute = (pathname: string): boolean =>
  EMPLOYEE_ROUTE_PREFIXES.some((p) => pathname === p || pathname.startsWith(p + '/'));

/** Whether an Access-panel-gated nav child may be shown.
 *
 *  Extracted from EmployeeNavPanel so the rule is assertable without mounting a
 *  router: it decides whether an admin surface appears, and "visible to everyone
 *  by accident" is exactly the failure worth a test.
 *
 *  `hasAccess` false means the viewer has no Access profile at all, which the
 *  app treats as fail-open everywhere — so this defers rather than inventing a
 *  stricter rule in the nav than the rest of the product uses. */
export const navChildPanelAllows = (
  child: EmployeeNavSubChild,
  ctx: { hasAccess: boolean; canManage: (key: string) => boolean },
): boolean => !child.panel || !ctx.hasAccess || ctx.canManage(child.panel);

/** The children to actually render under a parent.
 *
 *  A lone child pointing back at its own parent is not a submenu — it exists so
 *  a parent WITH real children still has a landing entry. When the real ones are
 *  all hidden, rendering it would give the item a chevron that expands to a
 *  duplicate of itself. */
export const submenuChildren = <T extends { to: string }>(
  parentTo: string,
  visible: T[],
): T[] => (visible.some((child) => child.to !== parentTo) ? visible : []);
